import React, { useState, useMemo } from "react";
import { faqData } from "../data/mockData";
import { ChevronDown } from "lucide-react";

// Safely serialize JSON-LD for inline <script> tag.
// Escapes "<" to prevent any breakout via strings ending in "</script>",
// even though our FAQ content is static and controlled.
const safeJsonLd = (obj) =>
  JSON.stringify(obj).replace(/</g, "\\u003c");

const FAQSchema = () => {
  const schemaHtml = useMemo(() => {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqData.map((item) => ({
        "@type": "Question",
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer,
        },
      })),
    };
    return { __html: safeJsonLd(schema) };
  }, []);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={schemaHtml}
    />
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-20 lg:py-28 bg-white">
      <FAQSchema />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-sm font-semibold text-[#3A7D44] uppercase tracking-wider">
            Common Questions
          </span>
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#1B2A1B] mt-3 mb-4"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            Frequently Asked Questions
          </h2>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqData.map((item, index) => {
            const isOpen = openIndex === item.question;
            const panelId = `faq-panel-${index}`;
            return (
              <div
                key={item.question}
                className="border border-gray-100 rounded-xl overflow-hidden hover:border-[#3A7D44]/20 transition-colors duration-200"
              >
                <button
                  onClick={() =>
                    setOpenIndex(isOpen ? null : item.question)
                  }
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-[#F1F8F2]/50 transition-colors"
                >
                  <span className="text-[#1B2A1B] font-medium pr-4">
                    {item.question}
                  </span>
                  <ChevronDown
                    size={20}
                    className={`text-[#3A7D44] flex-shrink-0 transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <div
                  id={panelId}
                  role="region"
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="px-6 pb-5 text-sm text-[#5A6B5A] leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
